import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { isInquirySaveConfirmed, UNCONFIRMED_INQUIRY_MESSAGE } from "../lib/inquiryConfirmation";
import { Reveal, SectionLabel } from "../components/Motion";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_OPTIONS = [
  "Coconut Shell Shisha Charcoal Briquettes",
  "BBQ Coconut Charcoal Briquettes",
  "Biomass & Sawdust",
  "Coconut Products",
  "Indonesian Coffee",
  "Indonesian Spices",
  "Cocoa",
  "Other Commodity",
];

const PRODUCT_ALIASES = {
  "Coconut Shell Shisha Charcoal": "Coconut Shell Shisha Charcoal Briquettes",
  "Coconut Shell BBQ Charcoal": "BBQ Coconut Charcoal Briquettes",
};

const PACKAGING_OPTIONS = ["1 KG", "10 KG", "Bulk", "Custom / Private Label", "Not Sure Yet"];
const INCOTERM_OPTIONS = ["EXW", "FOB", "CFR", "CIF", "Not Sure Yet"];
const PAYMENT_OPTIONS = [
  "Full Payment — T/T in Advance (100% before order processing / production begins)",
  "50% T/T Advance — 50% Before Container Stuffing",
];

const REQUEST_LABELS = {
  coa: "Requesting: Certificate of Analysis (COA)",
  sample: "Requesting: Product Sample",
  packaging: "Requesting: Packaging Discussion",
  specification: "Requesting: Product Specification",
  "private-label": "Requesting: Private Label / OEM Discussion",
};

const inputClass =
  "h-12 rounded-none border-charcoal/20 bg-white focus-visible:ring-forest focus-visible:ring-offset-0 text-sm";

function Field({ label, required, children, testId }) {
  return (
    <div data-testid={testId} className="space-y-2">
      <Label className="text-xs font-bold uppercase tracking-[0.18em] text-charcoal/60">
        {label} {required && <span className="text-clay">*</span>}
      </Label>
      {children}
    </div>
  );
}

export default function RequestQuote() {
  const [params] = useSearchParams();
  const requestKind = params.get("request") || "";
  const isSample = requestKind === "sample";
  const rawProduct = params.get("product") || "";

  const [form, setForm] = useState({
    full_name: "",
    company_name: "",
    country: "",
    email: "",
    phone: "",
    product: PRODUCT_ALIASES[rawProduct] || rawProduct,
    quantity: "",
    packaging: "",
    incoterm: "",
    destination_country: "",
    destination_port: "",
    specification: "",
    payment_term: "",
    delivery_date: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const requestNote = REQUEST_LABELS[requestKind];
  const set = (key) => (e) => setForm({ ...form, [key]: e.target ? e.target.value : e });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, request_type: isSample ? "sample" : "quotation" };
      if (requestNote && !isSample) {
        payload.message = [requestNote, form.message].filter(Boolean).join("\n\n");
      }
      const response = await axios.post(`${API}/api/inquiries`, payload);
      if (!isInquirySaveConfirmed(response)) {
        toast.error(UNCONFIRMED_INQUIRY_MESSAGE);
        return;
      }
      setSubmitted(true);
      window.scrollTo(0, 0);
      try {
        if (typeof window.gtag === "function") {
          window.gtag("event", "conversion", {
            send_to: "AW-18532564195/u_JGCKnG4qYdEOP5gYVF",
            value: 1.0,
            currency: "IDR"
          });
        }
      } catch (trackingError) {
        console.warn("Conversion tracking failed.");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(" ")
        : typeof detail === "string"
        ? detail
        : "Submission failed. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO />
      <div data-testid="request-quote-page" className="bg-offwhite pt-40 pb-24 sm:pb-32 min-h-screen">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
          <SectionLabel index="RFQ" title={isSample ? "Sample Request" : "Request a Quotation"} />

          {submitted ? (
            <Reveal className="mt-16 max-w-2xl">
              <div data-testid="quote-success-message" className="bg-forest text-offwhite p-10 sm:p-14">
                <CheckCircle2 className="w-10 h-10 mb-6" />
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Thank you.</h1>
                <p className="mt-5 text-base text-offwhite/75 leading-relaxed">
                  {isSample
                    ? "Your sample request has been received. Our team will review your requirements and respond with suitable options from Indonesia."
                    : "Your sourcing request has been received. Our team will review your requirements and respond with suitable sourcing options from Indonesia."}
                </p>
                <Link
                  to="/products"
                  data-testid="success-back-to-products"
                  className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-offwhite hover:text-clay transition-colors duration-300"
                >
                  Explore our products
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </Reveal>
          ) : (
            <div className="mt-8 grid lg:grid-cols-12 gap-14">
              <div className="lg:col-span-4">
                <Reveal>
                  <h1 data-testid="quote-primary-heading" className="font-display text-5xl sm:text-6xl font-black tracking-tight leading-[0.98]">
                    {isSample ? "Request a Sample" : "Request a Quotation"}
                  </h1>
                  <p className="mt-6 text-base text-charcoal/65 leading-relaxed">
                    {isSample
                      ? "Tell us which product you'd like to evaluate. Sample availability and shipping arrangements depend on the selected product and manufacturing partner."
                      : "Request your coconut charcoal quotation from Indonesia. Share the product specification, quantity, packaging and destination port for an FOB or CIF quotation."}
                  </p>
                  {requestNote && (
                    <p
                      data-testid="quote-request-note"
                      className="mt-6 inline-block text-xs font-bold uppercase tracking-[0.2em] text-forest border border-forest/40 px-3 py-2"
                    >
                      {requestNote}
                    </p>
                  )}
                  <div className="mt-10 space-y-4 text-sm text-charcoal/60">
                    <p className="flex gap-3">
                      <span className="w-1.5 h-1.5 rotate-45 bg-clay mt-2 shrink-0" />
                      Typical response includes specification confirmation, packaging options and FOB / CIF quotation.
                    </p>
                    <p className="flex gap-3">
                      <span className="w-1.5 h-1.5 rotate-45 bg-clay mt-2 shrink-0" />
                      Samples and COA can be arranged before order confirmation.
                    </p>
                  </div>
                </Reveal>
              </div>

              <Reveal delay={0.15} className="lg:col-span-8">
                <form
                  data-testid="quote-form"
                  onSubmit={handleSubmit}
                  className="bg-white border border-charcoal/10 p-6 sm:p-10 grid sm:grid-cols-2 gap-6"
                >
                  <Field label="Full Name" required testId="field-full-name">
                    <Input
                      data-testid="input-full-name"
                      required
                      value={form.full_name}
                      onChange={set("full_name")}
                      className={inputClass}
                      placeholder="Your full name"
                    />
                  </Field>
                  <Field label="Company Name" required testId="field-company-name">
                    <Input
                      data-testid="input-company-name"
                      required
                      value={form.company_name}
                      onChange={set("company_name")}
                      className={inputClass}
                      placeholder="Your company"
                    />
                  </Field>
                  <Field label="Country" required testId="field-country">
                    <Input
                      data-testid="input-country"
                      required
                      value={form.country}
                      onChange={set("country")}
                      className={inputClass}
                      placeholder="e.g. United Arab Emirates"
                    />
                  </Field>
                  <Field label="Business Email" required testId="field-email">
                    <Input
                      data-testid="input-email"
                      required
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      className={inputClass}
                      placeholder="name@company.com"
                    />
                  </Field>
                  <Field label="WhatsApp / Phone" testId="field-phone">
                    <Input
                      data-testid="input-phone"
                      value={form.phone}
                      onChange={set("phone")}
                      className={inputClass}
                      placeholder="+XX XXXX XXXX XXXX"
                    />
                  </Field>
                  <Field label="Product" required testId="field-product">
                    <Select required value={form.product} onValueChange={set("product")}>
                      <SelectTrigger data-testid="select-product" className={inputClass}>
                        <SelectValue placeholder="Select a product" />
                      </SelectTrigger>
                      <SelectContent>
                        {PRODUCT_OPTIONS.map((p) => (
                          <SelectItem
                            key={p}
                            value={p}
                            data-testid={`product-option-${p.toLowerCase().replace(/[\s/&]+/g, "-")}`}
                          >
                            {p}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label={isSample ? "Required Sample Quantity" : "Required Quantity"} required testId="field-quantity">
                    <Input
                      data-testid="input-quantity"
                      required
                      value={form.quantity}
                      onChange={set("quantity")}
                      className={inputClass}
                      placeholder={isSample ? "Example: 2 KG / 1 carton" : "Example: 1 x 40FT / 26 MT"}
                    />
                  </Field>
                  {isSample ? (
                    <>
                      <Field label="Destination Country" required testId="field-destination-country">
                        <Input
                          data-testid="input-destination-country"
                          required
                          value={form.destination_country}
                          onChange={set("destination_country")}
                          className={inputClass}
                          placeholder="e.g. Saudi Arabia"
                        />
                      </Field>
                      <Field label="Destination City / Port" testId="field-destination">
                        <Input
                          data-testid="input-destination"
                          value={form.destination_port}
                          onChange={set("destination_port")}
                          className={inputClass}
                          placeholder="Example: Jeddah"
                        />
                      </Field>
                    </>
                  ) : (
                    <>
                      <Field label="Packaging Requirement" testId="field-packaging">
                        <Select value={form.packaging} onValueChange={set("packaging")}>
                          <SelectTrigger data-testid="select-packaging" className={inputClass}>
                            <SelectValue placeholder="Select packaging" />
                          </SelectTrigger>
                          <SelectContent>
                            {PACKAGING_OPTIONS.map((p) => (
                              <SelectItem key={p} value={p} data-testid={`packaging-option-${p.toLowerCase().replace(/[\s/]+/g, "-")}`}>
                                {p}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Preferred Incoterm" testId="field-incoterm">
                        <Select value={form.incoterm} onValueChange={set("incoterm")}>
                          <SelectTrigger data-testid="select-incoterm" className={inputClass}>
                            <SelectValue placeholder="Select incoterm" />
                          </SelectTrigger>
                          <SelectContent>
                            {INCOTERM_OPTIONS.map((p) => (
                              <SelectItem key={p} value={p} data-testid={`incoterm-option-${p.toLowerCase().replace(/[\s/]+/g, "-")}`}>
                                {p}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Destination Port" testId="field-destination">
                        <Input
                          data-testid="input-destination"
                          value={form.destination_port}
                          onChange={set("destination_port")}
                          className={inputClass}
                          placeholder="Example: Jebel Ali, UAE"
                        />
                      </Field>
                      <Field label="Preferred Payment Term" required testId="field-payment">
                        <Select required value={form.payment_term} onValueChange={set("payment_term")}>
                          <SelectTrigger data-testid="select-payment" className={inputClass}>
                            <SelectValue placeholder="Select payment term" />
                          </SelectTrigger>
                          <SelectContent className="max-w-[calc(100vw-3rem)]">
                            {PAYMENT_OPTIONS.map((p) => (
                              <SelectItem
                                key={p}
                                value={p}
                                data-testid={`payment-option-${p.startsWith("Full") ? "full-tt-advance" : "50-50-stuffing"}`}
                                className="whitespace-normal leading-snug py-2.5"
                              >
                                {p}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Target Delivery Date" testId="field-delivery">
                        <Input
                          data-testid="input-delivery-date"
                          value={form.delivery_date}
                          onChange={set("delivery_date")}
                          className={inputClass}
                          placeholder="Example: October 2026"
                        />
                      </Field>
                    </>
                  )}
                  {!isSample && (
                    <div className="sm:col-span-2">
                      <Field label="Product Specification / Requirement" testId="field-specification">
                        <Textarea
                          data-testid="input-specification"
                          value={form.specification}
                          onChange={set("specification")}
                          className="rounded-none border-charcoal/20 bg-white focus-visible:ring-forest focus-visible:ring-offset-0 text-sm min-h-[110px]"
                          placeholder="Tell us your required size, shape, grade, packaging, monthly volume or other specifications."
                        />
                      </Field>
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <Field label={isSample ? "Additional Requirements" : "Additional Message"} testId="field-message">
                      <Textarea
                        data-testid="input-message"
                        value={form.message}
                        onChange={set("message")}
                        className="rounded-none border-charcoal/20 bg-white focus-visible:ring-forest focus-visible:ring-offset-0 text-sm min-h-[90px]"
                        placeholder={isSample ? "Shapes, grades or packaging you'd like to evaluate…" : "Anything else we should know?"}
                      />
                    </Field>
                  </div>
                  <div className="sm:col-span-2 pt-2">
                    <button
                      type="submit"
                      data-testid="quote-submit-button"
                      disabled={submitting}
                      className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-forest hover:bg-moss disabled:opacity-60 text-offwhite font-semibold px-10 py-4 text-sm tracking-wide transition-colors duration-300"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending…
                        </>
                      ) : (
                        <>
                          {isSample ? "Request a Sample" : "Request a Quote"}
                          <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </>
                      )}
                    </button>
                    <p data-testid="rfq-privacy-notice" className="mt-4 text-xs text-charcoal/45 leading-relaxed">
                      We use your information to review and respond to your inquiry. See our{" "}
                      <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" data-testid="rfq-privacy-policy-link"
                        className="text-forest underline underline-offset-2 hover:text-moss transition-colors duration-300">
                        Privacy Policy
                      </Link>.
                    </p>
                  </div>
                </form>
              </Reveal>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
